/**
 * Hilos de las notas GENERALES del historial (D66 del change `conversaciones-agente-ia`).
 *
 * Una nota general no admite respuestas de una persona, pero sí del asistente: cuando se lo arroba
 * en la nota de una ficha, contesta en el hilo de esa nota. La respuesta queda con `parentNoteId` =
 * la raíz y `eventId` null, y el timeline la trae como una nota general más —su fila no dice de
 * quién cuelga—. Lo que la vincula a su raíz sale del hilo (`GET /history/notes/:noteId/thread`).
 *
 * Este módulo arma eso sin React: qué notas vale la pena pedirle el hilo, qué respuestas cuelgan de
 * cada raíz y qué filas del timeline dejan de ir sueltas porque ya se dibujan debajo de la suya.
 */
import type { HistoryRow } from "@/services/history.api";

/** Una respuesta del hilo, con lo que hace falta para dibujarla: la forma de una nota por log. */
export interface NotaDelHilo {
  noteId: string;
  authorUserId: number;
  authorName: string;
  content: string;
  mentions: { userId: number; name: string }[];
  parentNoteId: string | null;
  createdAt: string;
}

/** Lo que devuelve `GET /history/notes/:noteId/thread`: la raíz y sus respuestas. */
export interface HiloDeNota {
  root: { noteId: string };
  replies: NotaDelHilo[];
  /** Una persona puede responder en el hilo (D66): una charla con el asistente. */
  canReply?: boolean;
}

export interface HilosArmados {
  /** Las respuestas de cada raíz, en orden de creación y sin repetidas. */
  respuestas: Map<string, NotaDelHilo[]>;
  /** La raíz de cada respuesta conocida. */
  raizDe: Map<string, string>;
}

/** Lo mínimo de una nota general para decidir si se le pide el hilo. */
export interface NotaGeneral {
  noteId: string;
  mentions?: { userId: number }[] | null;
  parentNoteId?: string | null;
}

/**
 * Las notas a las que hay que pedirles el hilo, sin repetir las ya pedidas.
 *
 * Sólo las que **arroban a alguien**: el asistente contesta a una nota general únicamente cuando se
 * lo arroba, y su respuesta arroba a quien pidió. Pedirle el hilo a la respuesta también sirve —el
 * hilo sube hasta la raíz—, y es lo que la encuentra cuando la raíz no está en la página. Una nota
 * que ya se sabe que es respuesta (`parentNoteId`) entra aunque no arrobe.
 */
export function notasParaPedirHilo(
  notas: NotaGeneral[],
  yaPedidas: ReadonlySet<string>,
): string[] {
  const out: string[] = [];
  const vistas = new Set<string>();
  for (const n of notas) {
    if (!n?.noteId || vistas.has(n.noteId) || yaPedidas.has(n.noteId)) continue;
    vistas.add(n.noteId);
    if ((n.mentions?.length ?? 0) > 0 || n.parentNoteId) out.push(n.noteId);
  }
  return out;
}

const porFecha = (a: NotaDelHilo, b: NotaDelHilo) =>
  a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : 0;

/**
 * Junta los hilos pedidos en respuestas por raíz.
 *
 * El mismo hilo puede llegar más de una vez —se le pidió a la raíz y a una respuesta que estaba en
 * la página—: las respuestas se juntan por `noteId`. Las filas del timeline que ya traen su
 * `parentNoteId` también cuentan, por si el hilo de su raíz todavía no llegó.
 */
export function armarHilos(
  hilos: HiloDeNota[],
  filas: HistoryRow[] = [],
): HilosArmados {
  const raizDe = new Map<string, string>();
  const porRaiz = new Map<string, Map<string, NotaDelHilo>>();
  const sumar = (raiz: string, nota: NotaDelHilo) => {
    if (nota.noteId === raiz) return;
    raizDe.set(nota.noteId, raiz);
    const notas = porRaiz.get(raiz) ?? new Map<string, NotaDelHilo>();
    if (!notas.has(nota.noteId)) notas.set(nota.noteId, nota);
    porRaiz.set(raiz, notas);
  };

  for (const h of hilos) {
    if (!h?.root?.noteId) continue;
    for (const r of h.replies ?? []) sumar(h.root.noteId, r);
  }

  // Filas sueltas con padre: se sube por los padres conocidos hasta una raíz.
  const padreDe = new Map<string, string>();
  for (const f of filas) {
    if (f.kind === "note" && f.payload.parentNoteId)
      padreDe.set(f.id, f.payload.parentNoteId);
  }
  for (const f of filas) {
    const padre = f.kind === "note" ? f.payload.parentNoteId : null;
    if (!padre) continue;
    let raiz = raizDe.get(f.id) ?? raizDe.get(padre) ?? padre;
    const vistas = new Set<string>([f.id]);
    while (padreDe.has(raiz) && !vistas.has(raiz)) {
      vistas.add(raiz);
      raiz = raizDe.get(raiz) ?? (padreDe.get(raiz) as string);
    }
    if (vistas.has(raiz)) continue; // un ciclo: no hay raíz, la fila sigue suelta
    sumar(raiz, {
      noteId: f.id,
      authorUserId: f.payload.authorUserId as number,
      authorName: f.payload.authorName ?? "",
      content: f.payload.content ?? "",
      mentions: f.payload.mentions ?? [],
      parentNoteId: padre,
      createdAt: f.payload.createdAt ?? f.occurredAt,
    });
  }

  const respuestas = new Map<string, NotaDelHilo[]>();
  for (const [raiz, notas] of porRaiz)
    respuestas.set(raiz, [...notas.values()].sort(porFecha));
  return { respuestas, raizDe };
}

/**
 * El timeline sin las respuestas que ya se dibujan debajo de su raíz.
 *
 * Una respuesta sale del stream sólo si su raíz **está en pantalla** —en el stream, en las fijadas
 * o en las programadas—. Si la raíz está en una página que todavía no se cargó, la respuesta queda
 * suelta: mejor eso que esconderla hasta que el operador llegue scrolleando.
 */
export function sacarRespuestasDelStream(
  filas: HistoryRow[],
  hilos: HilosArmados,
  raicesEnPantalla: ReadonlySet<string>,
): HistoryRow[] {
  return filas.filter((f) => {
    if (f.kind !== "note") return true;
    const raiz = hilos.raizDe.get(f.id);
    return !raiz || raiz === f.id || !raicesEnPantalla.has(raiz);
  });
}
